import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { PlanDetailsWidgetWrapper } from '@/bundle/_MasterAdmin/_BillingPage/ui/styles';
import { DetailsRow } from '@/components/DetailsWidget/DetailsRow/DetailsRow';
import { Button } from '@/components/Button/Button';
import LightningIcon from '@/images/LightningIcon.svg?react';
import { SubscriptionStatus } from '@/bundle/shared/components/SubscriptionStatus';
import { InternalOrganizationBillingDetailsType } from '@/api/v1/organization/internal/getInternalOrganizationBillingDetailsApi';
import { formatDate } from '@/helpers/dateHelpers/dateHelpers';
import {
  getBillingCycleLabel,
  getBillingFrequencyLabel,
  getBillingPrice,
  getBillingPriceLabel,
  getCancellationDateLabel,
} from '../helpers';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { envConfig } from '@/config/envConfig';
import { CONTACT_MOBILE_PHONE, FORMATTED_CONTACT_MOBILE_PHONE } from '@/const/shared';
import { SubscriptionPendingCancellationDate } from '@/bundle/shared/components/SubscriptionPendingCancellationDateStatus';

type PlanDetailsWidgetType = {
  billingDetails: InternalOrganizationBillingDetailsType;
  onCustomerPortalNavigate: () => void;
};

const getPendingCancellationTooltip = (date: string) => {
  return {
    name: 'cancellation-tooltip',
    content: (
      <>
        Your current subscription will be cancelled on {formatDate(date)}. <br />
        Until then, your organization will continue to have access based on your current subscription status.
      </>
    ),
    options: {
      style: {
        width: '512px',
      },
    },
  };
};

export const PlanDetailsWidget = ({ billingDetails, onCustomerPortalNavigate }: PlanDetailsWidgetType) => {
  const { isCancelledSubscription, isPendingCancellationSubscription } = useSubscriptionStatus();
  const { current_plan, subscription } = billingDetails;

  const billingFrequency = getBillingFrequencyLabel(current_plan);
  const billingPriceLabel = getBillingPriceLabel(current_plan);
  const billingPrice = getBillingPrice(current_plan);
  const cancellationDateLabel = getCancellationDateLabel(subscription, isPendingCancellationSubscription);
  const billingCycleLabel = getBillingCycleLabel(subscription, isPendingCancellationSubscription);
  const pendingCancellationTooltip = getPendingCancellationTooltip(subscription?.current_period_end);
  const showCancellationRow = isCancelledSubscription || isPendingCancellationSubscription;
  const showCancellationStatus = isPendingCancellationSubscription && !isCancelledSubscription;

  return (
    <PlanDetailsWidgetWrapper>
      <Box display='flex' flexDirection='column' height='100%'>
        <Box display='flex' flexDirection='column' rowGap='8px' marginBottom='14px'>
          <Box display='flex' flexDirection='column'>
            <Typography variant='subTitleSmall' color={COLORS.grey[950]} fontWeight='semibold'>
              {current_plan.name}
            </Typography>
            <Typography variant='bodySmall' color={COLORS.grey[650]}>
              Please contact{' '}
              <NavigationLink to={`mailto:${envConfig.SUPPORT_EMAIL_URL}`}>
                {envConfig.SUPPORT_EMAIL_URL}{' '}
              </NavigationLink>{' '}
              or call{' '}
              <NavigationLink to={`tel:${CONTACT_MOBILE_PHONE}`}>{FORMATTED_CONTACT_MOBILE_PHONE}</NavigationLink> to
              upgrade your plan.
            </Typography>
          </Box>

          <Box display='flex' flexDirection='column'>
            <DetailsRow
              name={
                <Typography variant='bodySmall' color={COLORS.black}>
                  Status
                </Typography>
              }
              offset='small'
            >
              <Box display='flex' alignItems='center' columnGap='6px'>
                <SubscriptionStatus status={subscription.status} />
                {showCancellationStatus && (
                  <SubscriptionPendingCancellationDate date={subscription.current_period_end} />
                )}
              </Box>
            </DetailsRow>
            <DetailsRow
              name={
                <Typography variant='bodySmall' color={COLORS.black}>
                  Billing Frequency
                </Typography>
              }
              offset='small'
            >
              {billingFrequency}
            </DetailsRow>
            <DetailsRow
              name={
                <Typography variant='bodySmall' color={COLORS.black}>
                  {billingPriceLabel}
                </Typography>
              }
              offset='small'
            >
              {billingPrice}
            </DetailsRow>
            <DetailsRow
              name={
                <Typography variant='bodySmall' color={COLORS.black}>
                  Billing Cycle
                </Typography>
              }
              offset='small'
            >
              {billingCycleLabel}
            </DetailsRow>
            {showCancellationRow && (
              <DetailsRow
                name={
                  <Typography variant='bodySmall' color={COLORS.black}>
                    Cancellation Details
                  </Typography>
                }
                offset='small'
                tooltip={isPendingCancellationSubscription && pendingCancellationTooltip}
              >
                {cancellationDateLabel}
              </DetailsRow>
            )}
          </Box>
        </Box>
        <Box marginTop='auto'>
          <Button size='medium' variant='primary' onClick={onCustomerPortalNavigate}>
            <Box display='flex' alignItems='center' columnGap='8px'>
              <LightningIcon />
              Manage Subscription
            </Box>
          </Button>
        </Box>
      </Box>
    </PlanDetailsWidgetWrapper>
  );
};
